import React, { useMemo } from "react";
import { Box, Typography } from "@material-ui/core";
import { alpha, useTheme } from "@material-ui/core/styles";

import useFormat from "../../../hooks/useFormat";
import { SENSOR_TYPE } from "../../../domain/sensor";
import I18nText from "../../../elements/I18nText";

const SensorValue = ({
  sensor,
  sensorSettings,
  colorOverride,
  className,
  thresholdSensor,
  isDeviceReported
}) => {
  const { formatNumber, formatUnit } = useFormat();
  const theme = useTheme();

  // Memoize all calculations
  const calculatedValues = useMemo(() => {
    const pct = sensor.pct;
    const threshold = sensor.threshold;
    const hasThreshold = sensor.pct <= sensor.threshold;

    const thresholdMode =
      thresholdSensor === "first" || thresholdSensor === "all"
        ? "below"
        : "above";
    const isHighBad = thresholdMode === "above";

    const isThresholdBreached = hasThreshold
      ? isHighBad
        ? pct >= threshold
        : pct <= threshold
      : false;

    // Determine colors
    let barColor =
      theme.custom.colors.sensors[sensor.contentType] ??
      theme.custom.colors.sensors[sensor.sequence];

    if (!colorOverride) {
      if (isThresholdBreached) {
        if (sensor.isInactive) {
          barColor = theme.palette.warning.main;
        } else {
          barColor = theme.palette.error.main;
        }
      }
    }

    return { pct, barColor };
  }, [
    sensor.pct,
    sensor.threshold,
    sensor.isInactive,
    thresholdSensor,
    colorOverride,
    theme.palette.warning.main,
    theme.palette.error.main
  ]);

  // Memoize formatted value
  const formattedValue = useMemo(() => {
    if (!isDeviceReported) {
      return "-";
    }
    let value = formatNumber(sensor.value);
    if (sensor.unit) {
      value += " " + formatUnit(sensor.unit);
    }
    return value;
  }, [sensor.value, sensor.unit, formatNumber, formatUnit, isDeviceReported]);

  // Memoize temperature value
  const formattedTemperature = useMemo(() => {
    if (!sensorSettings.showTemperatures || !sensor.temperatureValue) {
      return null;
    }
    return (
      formatNumber(sensor.temperatureValue) +
      " " +
      (formatUnit(sensor.temperatureUnit) || "°C")
    );
  }, [
    sensor.temperatureValue,
    sensor.temperatureUnit,
    sensorSettings.showTemperatures,
    formatNumber,
    formatUnit
  ]);

  // Memoize medium text
  const mediumText = useMemo(() => {
    if (!sensor.contentType || sensor.contentType === "") {
      return <I18nText>sensors.contentTypes.unknown</I18nText>;
    }
    return <I18nText>{`sensors.contentTypes.${sensor.contentType}`}</I18nText>;
  }, [sensor.contentType]);

  if (sensor.type === SENSOR_TYPE.COUNTER) {
    return (
      <Box className={className} px={1}>
        <Typography variant="body2" color="textPrimary">
          {formattedValue}
        </Typography>
      </Box>
    );
  }

  const { pct, barColor } = calculatedValues;

  return (
    <Box
      className={className}
      display="flex"
      flexDirection="column"
      flex={1}
      minWidth={0}
      px={1}
    >
      {/* Medium label above bar */}

      <Typography
        variant="caption"
        style={{
          fontWeight: 600,
          textAlign: "center",
          marginBottom: 4,
          textTransform: "capitalize",
          fontSize: "0.75rem"
        }}
        color="textPrimary"
      >
        {mediumText}
      </Typography>

      {/* Sensor bar container */}
      <Box
        position="relative"
        width="100%"
        height={25}
        borderRadius="8px"
        style={{
          backgroundColor: theme.custom.colors.sensorBar.bgColor,
          overflow: "hidden"
        }}
      >
        {/* Filled portion */}
        {sensorSettings.showVisuals && (
          <Box
            position="absolute"
            left={0}
            top={0}
            bottom={0}
            width={`${pct}%`}
            style={{
              backgroundColor: barColor,
              borderRadius: "8px",
              transition: "width 0.3s ease-in-out"
            }}
          />
        )}

        {/* Value text inside bar */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex={2}
        >
          <Typography
            variant="body2"
            style={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              backgroundColor: alpha(
                theme.custom.colors.sensorBar.valueBgColor,
                0.6
              ),
              borderRadius: "6px",
              padding: "0 8px",
              textShadow: pct > 20 ? "0 1px 2px rgba(0,0,0,0.2)" : "none",
              fontSize: "0.8rem"
            }}
          >
            {formattedValue}
          </Typography>
        </Box>
      </Box>
      {/*Temperature below bar*/}
      {formattedTemperature && (
        <Typography
          variant="caption"
          style={{
            textAlign: "center",
            marginTop: 4,
            fontSize: "0.7rem",
            fontWeight: 500
          }}
          color="textSecondary"
        >
          {formattedTemperature}
        </Typography>
      )}
    </Box>
  );
};

export default React.memo(SensorValue, (prevProps, nextProps) => {
  return (
    prevProps.sensor.id === nextProps.sensor.id &&
    prevProps.sensor.pct === nextProps.sensor.pct &&
    prevProps.sensor.value === nextProps.sensor.value &&
    prevProps.sensor.threshold === nextProps.sensor.threshold &&
    prevProps.sensor.temperatureValue === nextProps.sensor.temperatureValue &&
    prevProps.sensorSettings === nextProps.sensorSettings &&
    prevProps.colorOverride === nextProps.colorOverride &&
    prevProps.thresholdSensor === nextProps.thresholdSensor
  );
});
